import { FC, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { LoadingGroup, LoadingSkeleton, Message, Messages, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatNumberWithOrdinal } from "capitalrx-utilities";

import {
  useGetMemberPaymentConfigurationQuery,
  useListMedicarePrescriptionPaymentPlanInvoicesQuery,
} from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton } from "@/App/components";
import { useGetMedicarePrescriptionPaymentPlanFlag, useIsLoggedInUser } from "@/App/hooks";
import { usePlanYear } from "@/App/pages";
import { showMedicarePrescriptionPaymentPlanPaymentInfo } from "@/App/utils/featureFlags";

import { ModifyPaymentDateForm } from "./ModifyPaymentDateForm";
import { ModifyPaymentInformationForm } from "./ModifyPaymentInformationForm";
import { TransactionsPanel } from "./TransactionsPanel";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentInformationPanel";

export const PaymentInformationPanel: FC = () => {
  const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState<boolean>(false);
  const [isChangingPaymentDate, setIsChangingPaymentDate] = useState<boolean>(false);

  const planYear = usePlanYear();
  const { flag: memberFlag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);
  const { t: translate, i18n } = useTranslation();
  const { isLoggedInUser } = useIsLoggedInUser();

  const {
    data: paymentConfiguration,
    isLoading: isLoadingPaymentConfiguration,
    isError: isPaymentConfigurationError,
  } = useGetMemberPaymentConfigurationQuery(planYear);

  const {
    data: invoiceData,
    isLoading: isInvoiceLoading,
    isError: isInvoicesError,
  } = useListMedicarePrescriptionPaymentPlanInvoicesQuery({ plan_year: planYear });

  const hasBalance = useMemo(() => {
    return parseInt(invoiceData?.result_count) > 0;
  }, [invoiceData]);

  const showMemberHasNoBalanceMessage =
    showMedicarePrescriptionPaymentPlanPaymentInfo() && !isInvoiceLoading && paymentConfiguration && !hasBalance && !isInvoicesError;

  return (
    <Stack spacing="large" style={{ display: isPaymentConfigurationError && !memberFlag ? "none" : "flex" }}>
      <Heading>
        <Shelf>
          {translate(`${TRANSLATION_PATH}.paymentInformation`)}
          <Tag type="info" style={{ display: "inline-flex", alignItems: "center" }}>{planYear}</Tag>
          <Shelf.Spacer />
        </Shelf>
      </Heading>
      <LoadingGroup loading={isLoadingPaymentConfiguration}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack>
              <Stack spacing="mini">
                <LoadingSkeleton style={{ width: 80, height: 20 }} />
                <LoadingSkeleton style={{ width: 120, height: 20 }} />
              </Stack>
              <Stack spacing="mini">
                <LoadingSkeleton style={{ width: 120, height: 20 }} />
                <LoadingSkeleton style={{ width: 100, height: 20 }} />
              </Stack>
            </Stack>
          }
        >
          {!isPaymentConfigurationError && (
            <>
              <Stack spacing="medium">
                <Stack spacing="mini">
                  <Semibold>{translate(`${TRANSLATION_PATH}.paymentMethod`)}</Semibold>
                  {paymentConfiguration?.preferred_payment_method === "check" && <Body>{translate(`${TRANSLATION_PATH}.check`)}</Body>}
                  {paymentConfiguration?.preferred_payment_method === "card" && <Body>{translate(`${TRANSLATION_PATH}.card`)}</Body>}
                  {paymentConfiguration?.preferred_payment_method === "ach" && <Body>{translate(`${TRANSLATION_PATH}.ACH`)}</Body>}
                </Stack>
                {/* Disable these when emulating (and viewing PHI?) */}
                {!isChangingPaymentMethod && !isChangingPaymentDate && (
                  <Shelf>
                    <ThemedButton
                      testId="change-payment-method"
                      disabled={!isLoggedInUser || isLoadingPaymentConfiguration}
                      appearance="text"
                      onClick={() => setIsChangingPaymentMethod(true)}
                      style={{ padding: 0 }}
                    >
                      {translate(`${TRANSLATION_PATH}.changePaymentMethod`)}
                    </ThemedButton>
                    <Shelf.Spacer />
                  </Shelf>
                )}
              </Stack>
              {isChangingPaymentMethod && (
                <ModifyPaymentInformationForm
                  onCancel={() => setIsChangingPaymentMethod(false)}
                  onSubmit={() => setIsChangingPaymentMethod(false)}
                  paymentConfiguration={paymentConfiguration}
                />
              )}
              {paymentConfiguration?.preferred_payment_day && paymentConfiguration?.preferred_payment_method !== "check" && (
                <Stack spacing="medium">
                  <Stack spacing="mini">
                    <Semibold>{translate(`${TRANSLATION_PATH}.automaticPaymentDate`)}</Semibold>
                    <Body>
                      <Trans
                        i18nKey={`${TRANSLATION_PATH}.monthDayOption`}
                        values={{
                          month_day:
                            i18n.language === "en"
                              ? formatNumberWithOrdinal(parseInt(paymentConfiguration?.preferred_payment_day))
                              : paymentConfiguration?.preferred_payment_day,
                        }}
                      />
                    </Body>
                  </Stack>
                  <Shelf>
                    {!isChangingPaymentDate && !isChangingPaymentMethod && (
                      <ThemedButton
                        disabled={!isLoggedInUser || isLoadingPaymentConfiguration}
                        appearance="text"
                        onClick={() => setIsChangingPaymentDate(true)}
                        style={{ padding: 0 }}
                      >
                        {translate(`${TRANSLATION_PATH}.changeDate`)}
                      </ThemedButton>
                    )}
                    <Shelf.Spacer />
                  </Shelf>
                </Stack>
              )}
              {isChangingPaymentDate && (
                <ModifyPaymentDateForm onFormClose={() => setIsChangingPaymentDate(false)} paymentConfiguration={paymentConfiguration} />
              )}
            </>
          )}
          <Message visible={isPaymentConfigurationError && isLoggedInUser} appearance={Messages.Error}>
            {translate(`${TRANSLATION_PATH}.noPaymentConfigurationLoaded`)}
          </Message>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
      {showMemberHasNoBalanceMessage && (
        <Stack spacing="mini">
          <Body>{translate(`${TRANSLATION_PATH}.noAccumulatedBalance`)}</Body>
        </Stack>
      )}
      {isInvoicesError && (
        <Message visible appearance={Messages.Error}>
          {translate(`${TRANSLATION_PATH}.noInvoiceInformationLoaded`)}
        </Message>
      )}
      {showMedicarePrescriptionPaymentPlanPaymentInfo() && <TransactionsPanel />}
    </Stack>
  );
};
